import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import logoFull from '@/assets/logo-full.svg';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { AppFooter } from '@/components/layout/AppFooter';
import { LanguageSelector } from '@/components/LanguageSelector';

const languages = [{
  code: 'fr',
  name: 'Français',
  flag: '🇫🇷'
}, {
  code: 'en',
  name: 'English',
  flag: '🇬🇧'
}, {
  code: 'es',
  name: 'Español',
  flag: '🇪🇸'
}, {
  code: 'de',
  name: 'Deutsch',
  flag: '🇩🇪'
}, {
  code: 'it',
  name: 'Italiano',
  flag: '🇮🇹'
}];

const euCountryCodes = [
  { code: 'FR', flag: '🇫🇷', name: 'France' },
  { code: 'BE', flag: '🇧🇪', name: 'Belgique' },
  { code: 'DE', flag: '🇩🇪', name: 'Allemagne' },
  { code: 'ES', flag: '🇪🇸', name: 'Espagne' },
  { code: 'IT', flag: '🇮🇹', name: 'Italie' },
  { code: 'NL', flag: '🇳🇱', name: 'Pays-Bas' },
  { code: 'PT', flag: '🇵🇹', name: 'Portugal' },
  { code: 'AT', flag: '🇦🇹', name: 'Autriche' },
  { code: 'PL', flag: '🇵🇱', name: 'Pologne' },
  { code: 'LU', flag: '🇱🇺', name: 'Luxembourg' },
  { code: 'CH', flag: '🇨🇭', name: 'Suisse' },
  { code: 'GB', flag: '🇬🇧', name: 'Royaume-Uni' },
  { code: 'IE', flag: '🇮🇪', name: 'Irlande' },
  { code: 'DK', flag: '🇩🇰', name: 'Danemark' },
  { code: 'SE', flag: '🇸🇪', name: 'Suède' },
  { code: 'FI', flag: '🇫🇮', name: 'Finlande' },
  { code: 'GR', flag: '🇬🇷', name: 'Grèce' },
  { code: 'CZ', flag: '🇨🇿', name: 'Tchéquie' },
  { code: 'RO', flag: '🇷🇴', name: 'Roumanie' },
  { code: 'HU', flag: '🇭🇺', name: 'Hongrie' },
  { code: 'SK', flag: '🇸🇰', name: 'Slovaquie' },
  { code: 'BG', flag: '🇧🇬', name: 'Bulgarie' },
  { code: 'HR', flag: '🇭🇷', name: 'Croatie' },
  { code: 'SI', flag: '🇸🇮', name: 'Slovénie' },
  { code: 'EE', flag: '🇪🇪', name: 'Estonie' },
  { code: 'LV', flag: '🇱🇻', name: 'Lettonie' },
  { code: 'LT', flag: '🇱🇹', name: 'Lituanie' },
  { code: 'CY', flag: '🇨🇾', name: 'Chypre' },
  { code: 'MT', flag: '🇲🇹', name: 'Malte' },
];
const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    user,
    login,
    signup,
    resetPassword
  } = useAuth();
  
  const defaultTab = searchParams.get('tab') === 'signup' ? 'signup' : 'login';
  const {
    t,
    i18n
  } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [isVerifyingVat, setIsVerifyingVat] = useState(false);
  const [vatVerificationStatus, setVatVerificationStatus] = useState<{
    valid: boolean | null;
    message: string;
    alreadyRegistered: boolean;
  }>({
    valid: null,
    message: '',
    alreadyRegistered: false
  });

  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Signup form
  const [signupLanguage, setSignupLanguage] = useState('fr');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  const [signupFirstName, setSignupFirstName] = useState('');
  const [signupLastName, setSignupLastName] = useState('');
  const [signupPhone, setSignupPhone] = useState('');
  const [signupCompanyName, setSignupCompanyName] = useState('');
  const [signupFoundedDate, setSignupFoundedDate] = useState('');
  const [signupCompanyType, setSignupCompanyType] = useState<'shipper' | 'transporter' | 'driver'>('shipper');
  const [signupSiret, setSignupSiret] = useState('');
  const [signupCountryCode, setSignupCountryCode] = useState('FR');
  
  const [signupAcceptTerms, setSignupAcceptTerms] = useState(false);

  // Reset password
  const [resetEmail, setResetEmail] = useState('');

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);
  const verifyVATNumber = async (vatNumber: string, countryCode: string) => {
    if (!vatNumber || vatNumber.length < 8 || !countryCode) {
      setVatVerificationStatus({
        valid: null,
        message: '',
        alreadyRegistered: false
      });
      return;
    }
    setIsVerifyingVat(true);
    setVatVerificationStatus({
      valid: null,
      message: t('auth.signup.registrationNumberVerifying'),
      alreadyRegistered: false
    });
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('verify-vat', {
        body: {
          vatNumber,
          countryCode: countryCode.toUpperCase()
        }
      });
      if (error) throw error;
      if (data.alreadyRegistered) {
        setVatVerificationStatus({
          valid: false,
          message: t('auth.signup.registrationNumberExists'),
          alreadyRegistered: true
        });
      } else if (data.valid) {
        setVatVerificationStatus({
          valid: true,
          message: t('auth.signup.registrationNumberValid') + (data.companyName ? ` - ${data.companyName}` : ''),
          alreadyRegistered: false
        });
      } else {
        setVatVerificationStatus({
          valid: false,
          message: t('auth.signup.registrationNumberInvalid'),
          alreadyRegistered: false
        });
      }
    } catch (error) {
      console.error('Error verifying VAT:', error);
      setVatVerificationStatus({
        valid: null,
        message: '',
        alreadyRegistered: false
      });
    } finally {
      setIsVerifyingVat(false);
    }
  };
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error(t('auth.login.fillAllFields'));
      return;
    }
    setIsLoading(true);
    try {
      await login(loginEmail, loginPassword);
      toast.success(t('auth.login.success'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(t('auth.login.error'));
    } finally {
      setIsLoading(false);
    }
  };
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signupEmail || !signupPassword || !signupConfirmPassword || !signupCompanyName) {
      toast.error(t('auth.signup.fillRequired'));
      return;
    }
    if (!signupFoundedDate) {
      toast.error(t('auth.signup.foundedDateRequired'));
      return;
    }
    if (!signupAcceptTerms) {
      toast.error(t('auth.signup.termsRequired'));
      return;
    }
    if (vatVerificationStatus.alreadyRegistered) {
      toast.error(t('auth.signup.registrationNumberExists'));
      return;
    }
    if (signupPassword !== signupConfirmPassword) {
      toast.error(t('auth.signup.passwordMismatch'));
      return;
    }
    if (signupPassword.length < 6) {
      toast.error(t('auth.signup.passwordLength'));
      return;
    }
    setIsLoading(true);
    try {
      await signup({
        email: signupEmail,
        password: signupPassword,
        companyType: signupCompanyType,
        companyName: signupCompanyName,
        foundedDate: signupFoundedDate,
        firstName: signupFirstName,
        lastName: signupLastName,
        phone: signupPhone,
        siret: signupSiret,
        preferredLanguage: signupLanguage
      });
      toast.success(t('auth.signup.success'));
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Signup error:', error);
      toast.error(error.message || t('auth.signup.error'));
    } finally {
      setIsLoading(false);
    }
  };
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error(t('auth.resetPassword.enterEmail'));
      return;
    }
    setIsLoading(true);
    try {
      await resetPassword(resetEmail);
      toast.success(t('auth.resetPassword.success'));
      setShowResetPassword(false);
      setResetEmail('');
    } catch (error) {
      toast.error(t('auth.resetPassword.error'));
    } finally {
      setIsLoading(false);
    }
  };
  if (showResetPassword) {
    return <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md rounded-2xl shadow-lg">
          <CardHeader className="space-y-3 text-center">
            <div className="mx-auto mb-2">
              <img src={logoFull} alt="Velox" className="h-10" />
            </div>
            <CardTitle className="font-heading text-2xl text-secondary">
              {t('auth.resetPassword.title')}
            </CardTitle>
            <CardDescription>
              {t('auth.resetPassword.subtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('auth.resetPassword.email')}</Label>
                <Input id="reset-email" type="email" placeholder="votre@email.com" value={resetEmail} onChange={e => setResetEmail(e.target.value)} disabled={isLoading} required />
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? t('auth.resetPassword.loading') : t('auth.resetPassword.submit')}
              </Button>

              <Button type="button" variant="ghost" className="w-full" onClick={() => setShowResetPassword(false)} disabled={isLoading}>
                {t('auth.resetPassword.backToLogin')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>;
  }
  return <div className="min-h-screen bg-background flex flex-col">
      <div className="flex justify-end p-4">
        <LanguageSelector />
      </div>
      <div className="flex flex-1 items-center justify-center p-4 pt-0">

      <Card className="w-full max-w-2xl rounded-2xl shadow-lg">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto mb-2">
            <img src={logoFull} alt="Velox" className="h-10" />
          </div>
          <CardTitle className="font-heading text-2xl text-secondary">
            {t('auth.welcomeTitle')}
          </CardTitle>
          <CardDescription>
            {t('auth.login.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue={defaultTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login">{t('auth.login.title')}</TabsTrigger>
              <TabsTrigger value="signup">{t('auth.signup.title')}</TabsTrigger>
            </TabsList>

            {/* Login Tab */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('auth.login.email')}</Label>
                  <Input id="login-email" type="email" placeholder="votre@email.com" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} disabled={isLoading} required />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('auth.login.password')}</Label>
                  <Input id="login-password" type="password" placeholder="••••••••" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} disabled={isLoading} required />
                </div>

                <Button type="button" variant="link" className="h-auto p-0 text-sm" onClick={() => setShowResetPassword(true)} disabled={isLoading}>
                  {t('auth.login.forgotPassword')}
                </Button>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? t('auth.login.loading') : t('auth.login.submit')}
                </Button>
              </form>
            </TabsContent>

            {/* Signup Tab */}
            <TabsContent value="signup">
              <form onSubmit={handleSignup} className="space-y-3">
                {/* Langue - Full width */}
                <div className="space-y-2">
                  <Label htmlFor="signup-language" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Langue / Language *</Label>
                  <Select value={signupLanguage} onValueChange={value => {
                  setSignupLanguage(value);
                  i18n.changeLanguage(value);
                }} disabled={isLoading}>
                    <SelectTrigger id="signup-language">
                      <SelectValue>
                        <div className="flex items-center gap-2">
                          <span>{languages.find(l => l.code === signupLanguage)?.flag}</span>
                          <span>{languages.find(l => l.code === signupLanguage)?.name}</span>
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {languages.map(language => <SelectItem key={language.code} value={language.code}>
                          <div className="flex items-center gap-2">
                            <span>{language.flag}</span>
                            <span>{language.name}</span>
                          </div>
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Email et Téléphone - 2 columns */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="signup-email" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Email *</Label>
                    <Input id="signup-email" type="email" placeholder="votre@email.com" value={signupEmail} onChange={e => setSignupEmail(e.target.value)} disabled={isLoading} required />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-phone" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('auth.signup.phone')}</Label>
                    <Input id="signup-phone" type="tel" placeholder="+33 6 12 34 56 78" value={signupPhone} onChange={e => setSignupPhone(e.target.value)} disabled={isLoading} />
                  </div>
                </div>

                {/* Prénom et Nom - 2 columns */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="signup-first-name" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('auth.signup.firstName')}</Label>
                    <Input id="signup-first-name" type="text" placeholder="Jean" value={signupFirstName} onChange={e => setSignupFirstName(e.target.value)} disabled={isLoading} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-last-name" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('auth.signup.lastName')}</Label>
                    <Input id="signup-last-name" type="text" placeholder="Dupont" value={signupLastName} onChange={e => setSignupLastName(e.target.value)} disabled={isLoading} />
                  </div>
                </div>

                {/* Entreprise - Full width */}
                <div className="space-y-2">
                  <Label htmlFor="signup-company-name" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('auth.signup.companyName')} *</Label>
                  <Input id="signup-company-name" type="text" placeholder="Transport SA" value={signupCompanyName} onChange={e => setSignupCompanyName(e.target.value)} disabled={isLoading} required />
                </div>

                {/* Date d'existence */}
                <div className="space-y-2">
                  <Label htmlFor="signup-founded-date" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('auth.signup.foundedDate')} *</Label>
                  <Input id="signup-founded-date" type="date" max={new Date().toISOString().split('T')[0]} value={signupFoundedDate} onChange={e => setSignupFoundedDate(e.target.value)} disabled={isLoading} required />
                </div>


                {/* Type + Pays/SIRET - 2 columns */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Type entreprise */}
                  <div className="space-y-2">
                    <Label htmlFor="signup-company-type" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('auth.signup.companyType')} *</Label>
                    <Select value={signupCompanyType} onValueChange={value => setSignupCompanyType(value as 'shipper' | 'transporter' | 'driver')} disabled={isLoading}>
                      <SelectTrigger id="signup-company-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="shipper">{t('auth.signup.shipper')}</SelectItem>
                        <SelectItem value="transporter">{t('auth.signup.transporter')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Pays + SIRET */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('auth.signup.registrationNumber')}</Label>
                      {!isVerifyingVat && vatVerificationStatus.valid === true && <Badge variant="verified" className="flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          <span>{t('auth.signup.verified')}</span>
                        </Badge>}
                      {!isVerifyingVat && vatVerificationStatus.alreadyRegistered && <Badge variant="warning" className="flex items-center gap-1">
                          <XCircle className="h-3 w-3" />
                          <span>{t('auth.signup.alreadyExists')}</span>
                        </Badge>}
                      {isVerifyingVat && <Badge variant="secondary" className="flex items-center gap-1">
                          <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                          <span>{t('auth.signup.verifying')}</span>
                        </Badge>}
                    </div>
                    <div className="flex gap-2">
                      <Select 
                        value={signupCountryCode} 
                        onValueChange={(value) => {
                          setSignupCountryCode(value);
                          if (signupSiret && value) {
                            verifyVATNumber(signupSiret, value);
                          }
                        }}
                        disabled={isLoading}
                      >
                        <SelectTrigger className="w-[88px] flex-shrink-0">
                          <SelectValue>
                            {signupCountryCode && (
                              <div className="flex items-center gap-1.5">
                                <span>{euCountryCodes.find(c => c.code === signupCountryCode)?.flag}</span>
                                <span className="text-sm">{signupCountryCode}</span>
                              </div>
                            )}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {euCountryCodes.map(country => (
                            <SelectItem key={country.code} value={country.code}>
                              <div className="flex items-center gap-2">
                                <span>{country.flag}</span>
                                <span>{country.code}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input 
                        id="signup-siret" 
                        type="text" 
                        placeholder={t('auth.signup.registrationNumberPlaceholder')} 
                        value={signupSiret} 
                        onChange={e => {
                          setSignupSiret(e.target.value);
                          if (e.target.value && signupCountryCode) {
                            verifyVATNumber(e.target.value, signupCountryCode);
                          } else {
                            setVatVerificationStatus({
                              valid: null,
                              message: '',
                              alreadyRegistered: false
                            });
                          }
                        }} 
                        disabled={isLoading}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>


                {/* Mot de passe et Confirmer - 2 columns */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="signup-password" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('auth.signup.password')} *</Label>
                    <Input id="signup-password" type="password" placeholder="••••••••" value={signupPassword} onChange={e => setSignupPassword(e.target.value)} disabled={isLoading} required />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-confirm-password" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('auth.signup.confirmPassword')} *</Label>
                    <Input id="signup-confirm-password" type="password" placeholder="••••••••" value={signupConfirmPassword} onChange={e => setSignupConfirmPassword(e.target.value)} disabled={isLoading} required />
                  </div>
                </div>

                <div className="flex items-start space-x-2 pt-2">
                  <Checkbox 
                    id="signup-terms" 
                    checked={signupAcceptTerms}
                    onCheckedChange={(checked) => setSignupAcceptTerms(checked as boolean)}
                    disabled={isLoading}
                    required
                  />
                  <Label 
                    htmlFor="signup-terms" 
                    className="text-xs leading-relaxed cursor-pointer"
                  >
                    {t('auth.signup.acceptTermsPart1')}{' '}
                    <a 
                      href="/terms-of-service" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-accent hover:underline font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('auth.signup.termsLink')}
                    </a>
                    {' '}{t('auth.signup.acceptTermsPart2')}{' '}
                    <a 
                      href="/privacy-policy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-accent hover:underline font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('auth.signup.privacyLink')}
                    </a>{' *'}
                  </Label>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading || !signupAcceptTerms}>
                  {isLoading ? t('auth.signup.loading') : t('auth.signup.submit')}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      </div>
      <AppFooter variant="app" />
    </div>;
};
export default Auth;